import { Link } from 'react-router-dom';
import type { Apartment } from '@/mocks/apartments';

interface ApartmentCardProps {
  apartment: Apartment;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}

function formatPrice(price: number) {
  return price.toLocaleString('fr-FR');
}

function ApartmentCard({ apartment, isFavorite, onToggleFavorite }: ApartmentCardProps) {
  return (
    <div className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300 flex flex-col">
      {/* Image */}
      <Link to={`/apartments/${apartment.id}`} className="relative block overflow-hidden">
        <div className="h-52 md:h-56 w-full overflow-hidden">
          <img
            src={apartment.images[0]}
            alt={apartment.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
            apartment.transaction === 'location'
              ? 'bg-accent-500 text-foreground-950'
              : 'bg-primary-500 text-foreground-950'
          }`}>
            {apartment.transaction === 'location' ? 'À louer' : 'À vendre'}
          </span>
          {!apartment.available && (
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-foreground-800/80 text-foreground-100 whitespace-nowrap">
              Indisponible
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            onToggleFavorite(apartment.id);
          }}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-background-50/90 backdrop-blur-sm hover:bg-background-50 transition-colors"
          aria-label="Ajouter aux favoris"
        >
          <i className={`${isFavorite ? 'ri-heart-fill text-primary-400' : 'ri-heart-line text-foreground-500'} text-lg`} />
        </button>
      </Link>

      {/* Content */}
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground-50 leading-snug">
            {apartment.title}
          </h3>
        </div>

        <p className="flex items-center gap-1.5 text-sm text-foreground-400 mb-3">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-sm text-primary-400" />
          </span>
          {apartment.quartier}, {apartment.city}
        </p>

        <div className="flex items-center gap-4 text-sm text-foreground-400 mb-4">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-hotel-bed-line text-primary-400" />
            {apartment.bedrooms} ch.
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-drop-line text-primary-400" />
            {apartment.bathrooms} sdb
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <i className="ri-ruler-line text-primary-400" />
            {apartment.surface} m²
          </span>
        </div>

        <div className="mt-auto pt-3 border-t border-background-200/70 flex items-center justify-between">
          <div>
            <span className="font-heading font-bold text-lg text-primary-400">
              {formatPrice(apartment.price)} FCFA
            </span>
            <span className="block text-xs text-foreground-500">
              {apartment.transaction === 'location' ? '/ mois' : ''}
            </span>
          </div>
          <Link
            to={`/apartments/${apartment.id}`}
            className="px-4 py-2 text-sm font-semibold border border-primary-400/40 text-primary-400 rounded-md hover:bg-primary-500/10 transition-colors whitespace-nowrap"
          >
            Voir détails
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ApartmentCard;